// app\javascript\dashboard\routes\dashboard\bulkmessage\utils\randomInboxUtils.js

import { ref, computed } from 'vue';

export function useRandomInbox() {
  // State for random inbox modal
  const showRandomizeModal = ref(false);
  const randomizableInboxes = ref([]);
  const inboxPercentages = ref({});

  // Computed total percentage
  const totalPercentage = computed(() => {
    let total = 0;
    randomizableInboxes.value.forEach(inboxId => {
      total += inboxPercentages.value[inboxId] || 0;
    });
    return total;
  });

  // Open randomize modal
  const openRandomizeModal = () => {
    // Initialize with all inboxes deselected
    randomizableInboxes.value = [];
    // Clear previous percentages
    inboxPercentages.value = {};
    showRandomizeModal.value = true;
  };

  // Close randomize modal
  const closeRandomizeModal = () => {
    showRandomizeModal.value = false;
  };

  // Balance percentages automatically
  const balancePercentages = () => {
    const selected = randomizableInboxes.value;

    // If no inboxes selected, nothing to balance
    if (selected.length === 0) return;

    // Calculate equal percentage for each inbox
    const equalPercentage = Math.floor(100 / selected.length);
    let remainder = 100 % selected.length;

    // Reset all percentages and apply equal distribution
    selected.forEach((id, index) => {
      // Base equal value for all
      inboxPercentages.value[id] = equalPercentage;

      // Distribute remainder (if any) to first inboxes
      if (index < remainder) {
        inboxPercentages.value[id]++;
      }
    });
  };

  // Toggle selection of an inbox
  const toggleRandomizableInbox = inboxId => {
    const index = randomizableInboxes.value.indexOf(inboxId);
    if (index === -1) {
      // Add inbox
      randomizableInboxes.value.push(inboxId);
    } else {
      // Remove inbox
      randomizableInboxes.value.splice(index, 1);
    }

    // Always rebalance percentages after any change
    balancePercentages();
  };

  // Get percentage for a specific inbox
  const getInboxPercentage = inboxId => {
    return inboxPercentages.value[inboxId] || 0;
  };

  // Select all inboxes
  const selectAllInboxes = inboxOptions => {
    randomizableInboxes.value = inboxOptions.map(inbox => Number(inbox.value));
    balancePercentages(); // Balance percentages after selecting all
  };

  // Update percentage for a specific inbox
  const updateInboxPercentage = (inboxId, percentage) => {
    // Limit value between 1 and 100
    percentage = Math.max(1, Math.min(100, percentage));

    // Update percentage for this inbox
    inboxPercentages.value[inboxId] = percentage;

    // Adjust other inboxes
    const otherInboxes = randomizableInboxes.value.filter(id => id !== inboxId);
    if (otherInboxes.length === 0) return;

    const remainingPercentage = 100 - percentage;

    // Distribute remainder equally among other inboxes
    const perInbox = Math.floor(remainingPercentage / otherInboxes.length);
    let remainder = remainingPercentage % otherInboxes.length;

    otherInboxes.forEach((id, index) => {
      inboxPercentages.value[id] = perInbox;
      if (index < remainder) {
        inboxPercentages.value[id]++;
      }
    });
  };

  // Apply random inboxes distribution
  const applyRandomInboxes = (selectedRows, recipients, emit) => {
    console.log('[randomInboxUtils.applyRandomInboxes] Starting random distribution:', {
      timestamp: new Date().toISOString(),
      selectedRows: selectedRows.slice(0, 5), // Log first 5 for brevity
      totalSelectedRows: selectedRows.length,
      totalRecipients: recipients.length,
      inboxes: randomizableInboxes.value,
      percentages: inboxPercentages.value,
      totalPercentage: totalPercentage.value
    });
    
    // Check if we have selected inboxes and percentages sum to 100%
    if (
      randomizableInboxes.value.length === 0 ||
      totalPercentage.value !== 100
    ) {
      return false;
    }

    // Get recipient IDs to update (all selected or all if none selected)
    const recipientIds =
      selectedRows.length > 0 ? [...selectedRows] : recipients.map(r => r.id);

    // Exit if no recipients
    if (recipientIds.length === 0) return false;

    // Shuffle the recipient IDs array to randomize distribution
    const shuffledRecipientIds = [...recipientIds].sort(
      () => Math.random() - 0.5
    );

    // Calculate how many recipients should go to each inbox
    const inboxAssignments = {};
    let currentIndex = 0;

    randomizableInboxes.value.forEach(inboxId => {
      const percentage = inboxPercentages.value[inboxId] || 0;
      // Calculate exact number of recipients for this inbox based on percentage
      const recipientCount = Math.round(
        (percentage / 100) * shuffledRecipientIds.length
      );

      if (!inboxAssignments[inboxId]) {
        inboxAssignments[inboxId] = [];
      }

      // Assign the calculated number of recipients to this inbox
      for (
        let i = 0;
        i < recipientCount && currentIndex < shuffledRecipientIds.length;
        i++
      ) {
        const recipientId = shuffledRecipientIds[currentIndex];
        inboxAssignments[inboxId].push(recipientId);

        // Update recipient's inbox
        emit('update-inbox', recipientId, inboxId);

        currentIndex++;
      }
    });

    // Handle any remaining recipients due to rounding (assign to the last inbox)
    if (currentIndex < shuffledRecipientIds.length) {
      const lastInboxId =
        randomizableInboxes.value[randomizableInboxes.value.length - 1];

      while (currentIndex < shuffledRecipientIds.length) {
        const recipientId = shuffledRecipientIds[currentIndex];
        inboxAssignments[lastInboxId].push(recipientId);

        // Update recipient's inbox
        emit('update-inbox', recipientId, lastInboxId);

        currentIndex++;
      }
    }

    // Emit an event to update the global selectedInboxes with all randomized inboxes
    // This ensures the parent component knows all inboxes being used for random distribution
    console.log('[randomInboxUtils.applyRandomInboxes] Random distribution complete:', {
      timestamp: new Date().toISOString(),
      totalRecipientsUpdated: recipientIds.length,
      inboxesUsed: randomizableInboxes.value
    });
    emit('update-random-inboxes', randomizableInboxes.value);

    // Close modal
    closeRandomizeModal();
    return true;
  };

  return {
    // State
    showRandomizeModal,
    randomizableInboxes,
    inboxPercentages,
    totalPercentage,

    // Methods
    openRandomizeModal,
    closeRandomizeModal,
    balancePercentages,
    toggleRandomizableInbox,
    getInboxPercentage,
    selectAllInboxes,
    updateInboxPercentage,
    applyRandomInboxes,
  };
}
